import { type FC, PropsWithChildren, useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { AUTOMATION_STEPS, getStepConfig } from 'pages/AutomationEngine/steps';
import { KizenTypography } from '__app/typography';
import styled from '@emotion/styled';
import { useTooltip } from '__components/Kizen/Tooltip';
import { HistoryPanel } from './History/HistoryPanel';
import { useExecutionHistory } from './History/useExecutionHistory';
import { formatAmount } from './formatAmount';
import { useAutomationsSelector } from 'pages/AutomationEngine/store/react';
import type { KizenNodeType, Step } from 'pages/AutomationEngine/types';
import type { FlowNodeType } from 'pages/AutomationEngine/Engine/Flow/types';
import {
  getFocusStep,
  getFocusStepBySearch,
} from 'pages/AutomationEngine/store/selectors';
import { PluginContext } from 'ts-components/Plugins/context';
import type { StepMovingBlockerTypes } from 'pages/AutomationEngine/store/types';
import { STEP_SLOT_HEIGHT } from '../constants';

const StyledStat = styled.div`
  border-radius: 4px;
  padding: 5px;
  display: flex;
  flex-direction: column;
  gap: 5px;
  align-items: center;
  background-color: var(--color-background-lighter);
  flex: 1 0 40px;
`;

const StatsWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
`;

type StatsEntry = {
  numberCompleted?: number;
  numberPending?: number;
  numberPaused?: number;
  numberFailed?: number;
  numberYes?: number;
  numberNo?: number;
  numberMet?: number;
  numberNotMet?: number;
};

const defaultStats = {
  action: { numberCompleted: 0, numberFailed: 0 },
  delay: { numberCompleted: 0, numberPending: 0 },
  trigger: { numberCompleted: 0 },
  condition: { numberCompleted: 0, numberFailed: 0, numberYes: 0, numberNo: 0 },
  goal: { numberCompleted: 0, numberPending: 0, numberMet: 0, numberNotMet: 0 },
  default: {},
  test: {
    numberCompleted: 9999,
    numberPending: 9999,
    numberPaused: 9999,
    numberFailed: 9999,
    numberYes: 9999,
    numberNo: 9999,
    numberMet: 9999,
    numberNotMet: 9999,
  },
};

type StatsBlockProps = {
  type: string;
  title: string;
  amount: number;
};

const useStats = (
  statsProp: StatsEntry,
  stepType: keyof typeof defaultStats
) => {
  const { t } = useTranslation();

  return useMemo(() => {
    const stats = statsProp || defaultStats[stepType || 'default'];
    const blocks: StatsBlockProps[] = [
      {
        type: 'completed',
        title: stepType === 'trigger' ? t('Triggered') : t('Completed'),
        amount: stats.numberCompleted || 0,
      },
    ];

    if (stats.numberPending) {
      blocks.push({
        type: 'pending',
        title: t('Pending'),
        amount: stats.numberPending,
      });
    }

    if (stats.numberPaused) {
      blocks.push({
        type: 'paused',
        title: t('Paused'),
        amount: stats.numberPaused,
      });
    }

    blocks.push({
      type: 'failed',
      title: t('Failed'),
      amount: stats.numberFailed || 0,
    });

    switch (stepType) {
      case 'goal': {
        blocks.push(
          {
            type: 'met',
            title: t('Met'),
            amount: stats.numberMet || 0,
          },
          {
            type: 'notMet',
            title: t('Not Met'),
            amount: stats.numberNotMet || 0,
          }
        );

        break;
      }
      case 'condition': {
        blocks.push(
          {
            type: 'yes',
            title: t('Yes'),
            amount: stats.numberYes || 0,
          },
          {
            type: 'no',
            title: t('No'),
            amount: stats.numberNo || 0,
          }
        );
        break;
      }
      default:
        break;
    }

    return blocks;
  }, [statsProp, stepType, t]);
};

const StatsBlock: FC<StatsBlockProps> = ({ type, title, amount }) => {
  const [tooltipProps, tooltip] = useTooltip(
    {
      label: `${amount} ${title}`,
    },
    () => true
  );
  const parsedAmount = useMemo(() => formatAmount(amount), [amount]);

  const color =
    type === 'failed'
      ? 'var(--color-agentic-workflow-error-primary)'
      : undefined;

  return (
    <StyledStat key={type} {...tooltipProps}>
      {tooltip}
      <KizenTypography as="span" weight="bold" color={color}>
        {parsedAmount}
      </KizenTypography>
      <KizenTypography as="span" size="8px" weight="bold" color={color}>
        {title}
      </KizenTypography>
    </StyledStat>
  );
};

const Stats: FC<
  PropsWithChildren & { stats: StatsEntry; stepType: keyof typeof defaultStats }
> = ({ stats, stepType }) => {
  const statBlocks = useStats(stats, stepType);
  return (
    <StatsWrapper data-qa="card-panel">
      {statBlocks.map(({ type, title, amount }) => (
        <StatsBlock key={type} type={type} title={title} amount={amount} />
      ))}
    </StatsWrapper>
  );
};

export const ConfiguredStepCard: FC<{
  type: FlowNodeType;
  step: Step;
  showStats?: boolean;
  disableMenu?: boolean;
  scale?: number | null;
  onStart?: (() => void) | null;
  onStop?: (() => void) | null;
  showMovingPlaceholder?: boolean;
  showMovingBlocker: StepMovingBlockerTypes;
  onEditUserDescription: (userDescription: string) => void;
  [key: string]: any;
}> = ({
  type,
  step,
  showStats = false,
  disableMenu = false,
  scale: scaleProp = null,
  onStart: onStartProp = null,
  onStop: onStopProp = null,
  showMovingPlaceholder = false,
  showMovingBlocker = false,
  onEditUserDescription,
  ...others
}) => {
  const { t } = useTranslation();
  const { businessPluginAppsByApiName, businessPluginAppsById } =
    useContext(PluginContext);

  const stepType =
    step.type === AUTOMATION_STEPS.goal.type
      ? (step.details?.type as KizenNodeType)
      : step.type;
  const { stats } = step;
  const stepConfig = getStepConfig(stepType);

  const {
    historyId,
    borderColor,
    subtypeLabel,
    CardComponent,
    menuOptions,
    historyMode,
    addMenu,
    unusedHistoryStep,
    focusedHistoryStep,
    onStart,
    onStop,
    scale,
    showErrors,
    unreachable,
    showRestart,
    executionMode,
  } = useExecutionHistory({
    step,
    type,
    showMovingPlaceholder,
    showMovingBlocker,
    onStartProp,
    onStopProp,
    scaleProp,
  });

  const focusStep: string | null = useAutomationsSelector(getFocusStep);
  const focusStepBySearch: string | null =
    useAutomationsSelector(getFocusStepBySearch);
  const searchMatchedSteps = useAutomationsSelector(
    ({ searchMatchedSteps }) => searchMatchedSteps
  );

  const matchedBySearch = searchMatchedSteps.includes(step.id);

  const focusedBySearch =
    focusStep === step.id && focusStepBySearch === step.id && matchedBySearch;
  const isFocused =
    focusedBySearch || (!focusStepBySearch && focusedHistoryStep);

  // prepare stats line
  const statsComponent = useMemo(() => {
    if (historyId && step?.history) {
      return <HistoryPanel historyList={step.history} step={step} />;
    }
    if (stepConfig?.goalType) {
      return <Stats stats={stats} stepType="goal" />;
    }
    if (stepConfig?.type === 'delay' || stepConfig?.type === 'condition') {
      return <Stats stats={stats} stepType={stepConfig.type} />;
    }
    if (type === 'automationStep') {
      return <Stats stats={stats} stepType="action" />;
    }
    if (type === 'automationTrigger' && stepConfig.type !== 'add_trigger') {
      return <Stats stats={stats} stepType="trigger" />;
    }
    // default card stats value
    return ``;
  }, [step, historyId, stepConfig?.goalType, stepConfig.type, type, stats]);

  const badgeProps = {
    color: stepConfig.color,
    bgColor: stepConfig.bgColor,
    label: stepConfig.label?.(t),
    icon: stepConfig.icon,
  };

  return (
    <CardComponent
      badgeProps={badgeProps}
      subtypeLabel={subtypeLabel}
      getStepSlotHeight={
        showStats && !executionMode
          ? STEP_SLOT_HEIGHT.stats
          : historyId
            ? STEP_SLOT_HEIGHT.history
            : STEP_SLOT_HEIGHT.edit
      }
      data-qa-error={showErrors}
      borderColor={matchedBySearch ? 'var(--color-border-dark)' : borderColor}
      panel={showStats || (historyId && step?.history) ? statsComponent : null}
      userDescription={step.userDescription}
      menuOptions={menuOptions}
      flashBackgroundColor={step.flashBackgroundColor}
      disableMenu={
        disableMenu || (!stepConfig.creatable && !stepConfig.deletable)
      }
      {...(stepConfig.CardComponent && { type, step })}
      {...(stepConfig.movable && {
        scale,
        onStart,
        onStop,
      })}
      {...others}
      onEditUserDescription={onEditUserDescription}
      addMenu={addMenu}
      scale={scale}
      onStart={onStart}
      onStop={onStop}
      historyMode={historyMode}
      unusedHistoryStep={
        unusedHistoryStep || step.shouldSkipExecution || unreachable
      }
      focusedHistoryStep={isFocused}
      showRestart={showRestart}
      showStepId={executionMode}
      stepLabelType={
        unreachable
          ? 'unreachable'
          : step.shouldSkipExecution
            ? 'skipped'
            : step.changeType
      }
      pluginApp={
        (
          businessPluginAppsByApiName[step.details?.pluginAppApiName] ||
          businessPluginAppsById[
            step.details?.businessPluginAppId ||
              step.details?.llmDecision?.businessPluginAppId
          ]
        )?.plugin_app
      }
    />
  );
};
